import posthog, { isPostHogEnabled } from './posthog';
import { getChartIdentity, getAnalyticsUserSnapshot } from './chartIdentity';
import { getToolEntitlement } from './toolTier';
import { getChartsAttributionContext } from './chartsAttribution';
import { resolveToolId } from './toolCatalog';
import packageJson from '../../package.json';

function capture(eventName, properties = {}) {
  if (!isPostHogEnabled || !posthog?.capture) {
    return;
  }
  const sessionId = posthog.get_session_id?.();
  const attribution = getChartsAttributionContext();
  const user = getAnalyticsUserSnapshot();
  posthog.capture(eventName, {
    app_version: packageJson.version,
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(user.plan_tier ? { plan_tier: user.plan_tier } : {}),
    ...(user.subscription_status ? { subscription_status: user.subscription_status } : {}),
    ...(attribution.handoff_id ? { handoff_id: attribution.handoff_id } : {}),
    ...properties,
  });
}

function currentToolContext() {
  if (typeof window === 'undefined') {
    return {};
  }
  const destination_path = `${window.location.pathname}${window.location.search}`;
  const identity = getChartIdentity(window.location.pathname, window.location.search);
  return {
    tool_id: resolveToolId(destination_path),
    tool_name: identity?.chart_name,
    tool_category: identity?.chart_type,
    required_tier: getToolEntitlement(destination_path),
    destination_path,
    ...(identity?.symbol ? { instrument: identity.symbol, symbol: identity.symbol } : {}),
  };
}

export function captureChartsPaywallShown(extra = {}) {
  capture('charts_paywall_shown', {
    ...currentToolContext(),
    paywall_trigger: extra.paywall_trigger || 'feature_gate',
    upgrade_target: extra.upgrade_target || 'professional',
    ...extra,
  });
}

export function captureChartsUpgradeClicked(extra = {}) {
  capture('charts_upgrade_clicked', {
    ...currentToolContext(),
    cta_surface: extra.cta_surface || 'paywall_modal',
    upgrade_target: extra.upgrade_target || 'professional',
    ...extra,
  });
}

export function captureChartsPricingViewed(extra = {}) {
  capture('charts_pricing_viewed', {
    pricing_surface: extra.pricing_surface || 'pricing',
    ...extra,
  });
}

export function captureChartsCheckoutStarted(extra = {}) {
  capture('charts_checkout_started', {
    payment_provider: extra.payment_provider,
    plan_name: extra.plan_name,
    plan_tier: extra.plan_tier,
    ...extra,
  });
}

export function captureChartsSubscriptionStarted(extra = {}) {
  capture('charts_subscription_started', {
    plan_name: extra.plan_name,
    plan_tier: extra.plan_tier,
    ...extra,
  });
}

export function captureChartsPaymentSucceeded(extra = {}) {
  capture('charts_payment_succeeded', {
    plan_name: extra.plan_name,
    order_id: extra.order_id,
    payment_provider: extra.payment_provider,
    ...extra,
  });
}

export function captureChartsPaymentFailed(extra = {}) {
  capture('charts_payment_failed', {
    plan_name: extra.plan_name,
    order_id: extra.order_id,
    payment_provider: extra.payment_provider,
    failure_reason: extra.failure_reason || 'unknown',
    ...extra,
  });
}

export function captureChartsReplayStarted(extra = {}) {
  capture('charts_replay_started', {
    ...currentToolContext(),
    ...extra,
  });
}

export function captureChartsReplayCompleted(extra = {}) {
  capture('charts_replay_completed', {
    ...currentToolContext(),
    ...extra,
  });
}

export function planTierFromPlanName(planName) {
  if (!planName) {
    return undefined;
  }
  const normalized = String(planName).toUpperCase();
  if (normalized.includes('ELITE')) {
    return 'elite';
  }
  if (normalized.includes('PROFESSIONAL') || normalized.includes('PREMIUM') || normalized.includes('PRO')) {
    return 'professional';
  }
  return undefined;
}
